<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import type { CoinHistoryAggregatedResponse, PeriodNameResponse, UserConfig } from '@/lib/interfaces/calculator';
import { useBrowserLocation, useLocalStorage } from '@vueuse/core'
import type { PlanBase, PlanName } from '@/lib/interfaces/pricing';
import plansBase from '@/lib/plansBase';
import { computed } from 'vue';
import makeConfigLoadedUrl from '@/lib/makeConfigLoadedUrl';
import { availableCurrencies, type CurrencyName } from '@consts';
import formatRoi from '@/lib/formatRoi';
import formatNumberBasedOnSize from '@/lib/formatNumberBasedOnSize';

interface Props {
  response?: CoinHistoryAggregatedResponse | undefined,
  hasUnattendedUserConfigChange: boolean
  userConfig: UserConfig
}

const props = defineProps<Props>()
const userConfigPeriod = defineModel('period', { required: true })
const selectedPerformanceDisplay = useLocalStorage<'%' | 'x'>('selected-performance-display', 'x', {initOnMounted: true})

const periods: Record<PeriodNameResponse, number> = {
  "3-months": 90,
  "6-months": 180,
  "12-months": 365,
}

const location = useBrowserLocation()

const recommendedPlan = computed(() => props.response ? getRecommendedPlan(props.response.result.recommendedPlan) : undefined)

function getRecommendedPlan(planNo: number): PlanBase {

  const indexablePlans = [
    'basic',
    'enthusiast',
    'whale',
  ] as const satisfies PlanName[]

  const planName = indexablePlans[planNo - 1]
  if(!planName) throw new Error('Server recommended plan not found')

  return plansBase[planName]

}

const configLoadedUrl = computed(() => makeConfigLoadedUrl({
  userConfig: props.userConfig,
  plan: props.response && getRecommendedPlan(props.response.result.recommendedPlan).name,
  baseUrl: location.value.origin + '/pricing'
}))

const selectedCurrency = useLocalStorage<CurrencyName>('selected-currency', availableCurrencies.eur.name)

</script>

<template>
  <div class="pt-3 relative">
    <!-- circles -->
    <div class="absolute -left-1/2 -top-1/2 animate-spin animate-duration-16000 origin-c flex items-center justify-center h-120 w-120">
      <div class="inset-0 rounded-full border-2 border-brand-primary/5 absolute -translate-x-2 scale-y-120" />
      <div class="inset-0 rounded-full border-2 border-brand-primary/5 absolute -translate-x-8" />
      <div class="inset-0 rounded-full border-2 border-brand-primary/5 absolute -translate-x-16" />
      <div class="inset-0 rounded-full border-2 border-brand-primary/5 absolute -translate-x-32" />
    </div>
    <div 
      :class="[
        'bg-effect-lighting bg-brand-gradient',
        'absolute -top-1/2 -left-1/4',
        'h-180 w-180',
      ]" 
    />
    <div class="flex justify-center">
      <div class="relative">
        <Listbox 
          v-if="userConfigPeriod" 
          v-model="userConfigPeriod"
        >
          <ListboxButton class="flex justify-center font-semibold text-xs h-3.5">
            <Transition
              enter-active-class="duration-400 delay-200 ease-in-out"
              leave-active-class="duration-300 delay-200 ease-in-out"
              enter-from-class="translate-y-2 opacity-0"
              leave-to-class="-translate-y-2 opacity-0"
              mode="out-in"
            >
              <span 
                class="inline-block text-brand-gradient"
                v-if="props.response && !props.hasUnattendedUserConfigChange" 
              >
                <span>
                  {{ userConfigPeriod === 'auto' ? props.response.result.period : userConfigPeriod }} period
                </span>
              </span>
            </Transition>
          </ListboxButton>
          <ListboxOptions :class="[
            'w-max divide-y-2 divide-brand-stroke/10 mt-2 z-10 absolute bg-brand-background',
            'border-2 border-brand-primary rounded-3'
          ]">
            <ListboxOption 
              :class="[
                'flex justify-between items-center cursor-pointer text-sm',
                'hover:bg-brand-stroke/10'
              ]" 
              v-for="(period, index) in Object.keys(periods)" 
              :key="index" 
              :value="period"
            >
              <div class="py-4 px-4 flex items-center gap-x-2.5">
                {{ period }}
              </div>
              <!-- <span v-if="selectedCurrency.label === capital.label" class="mr-4 opacity-40 i-ep:select" /> -->
            </ListboxOption>
          </ListboxOptions>
        </Listbox>
      </div>
    </div>
    <div 
      class="relative"
      role="region"
      aria-live="polite"
    >
      <div class="sr-only">
        <template v-if="props.response && !props.hasUnattendedUserConfigChange">
          {{ $t('calculation_results') }}:
        </template>
        <template v-else>
          {{ $t('calculating') }}...
        </template>
      </div>

      <!-- ligthing effect start -->
      <div :class="[
        'bg-effect-lighting bg-brand-gradient -z-5 opacity-40',
        'absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2',
        'aspect-video w-[200%]',
      ]" />
      <!-- ligthing effect end -->
      <div class="mt-4 sm:mt-16" >
        <Transition
          enter-active-class="duration-400 delay-200 ease-in-out"
          leave-active-class="duration-300 delay-200 ease-in-out"
          enter-from-class="translate-y-2 opacity-0"
          leave-to-class="-translate-y-2 opacity-0"
          mode="out-in"
        >
          <div 
            v-if="props.response && !props.hasUnattendedUserConfigChange"
            class="flex items-center gap-x-2"
          >
              <span 
                :class="[
                  'text-2xl bg-brand-gradient',
                  props.response.result.profitPercentage < 0 ? 'i-ph:trend-down-fill' : 'i-ph:trend-up-fill'
                ]" 
              />
              <span class="text-brand-gradient font-bold">
                {{ formatRoi(props.response.result.profitPercentage, selectedPerformanceDisplay) }}
                <span class="sr-only">
                  {{ $t('total_expected_profit_at_the_end_of') }} {{ props.response.result.period }} {{ $t('period_based_off_of_historical_backtest_performances')}}
                </span>
              </span>
          </div>
          <div 
            v-else
            class="text-brand-gradient font-bold"
          >
            {{ $t('calculating') }}...
          </div>
        </Transition>        
      </div>
      <div class="flex sm:flex-row flex-col sm:items-center gap-x-5">
        <div class="font-bold text-5xl xl:text-6xl leading-[150%]">
          
          <Transition
            enter-active-class="duration-100 ease-in-out"
            leave-active-class="duration-300 ease-in-out"
            enter-from-class="translate-y-2 opacity-0"
            leave-to-class="-translate-y-2 opacity-0"
            mode="out-in"
          >
            <span 
              v-if="props.response && !props.hasUnattendedUserConfigChange"
              class="inline-block text-brand-gradient"
            >
              {{ availableCurrencies[selectedCurrency].sign }} {{ formatNumberBasedOnSize(props.response.result.profit + props.response.initialAmount) }}
              <span class="sr-only">
                {{ $t('total_expected_profit_at_the_end_of') }} {{ props.response.result.period }} {{ $t('period_based_off_of_historical_backtest_performances') }}
              </span>
            </span>
            <span 
              v-else 
              aria-hidden="true"
              class="inline-block text-brand-gradient"
            >
              00,000.00
            </span>
          </Transition>
          
        </div>
        <div class="space-y-1 font-semibold text-xs text-white">
          <Transition
            enter-active-class="duration-400 delay-800 ease-in-out"
            leave-active-class="duration-200 ease-in-out"
            enter-from-class="translate-y-2 opacity-0"
            leave-to-class="-translate-y-2 opacity-0"
          >
            <div v-if="props.response && !props.hasUnattendedUserConfigChange" >~{{ Math.floor( props.response.result.tradeFrequency / 30) }} {{$t('trades')}} / {{$t('day')}}</div>
          </Transition>
          <Transition
            enter-active-class="duration-400 delay-1000 ease-in-out"
            leave-active-class="duration-200 ease-in-out"
            enter-from-class="translate-y-2 opacity-0"
            leave-to-class="-translate-y-2 opacity-0"
          >
            <div v-if="props.response && !props.hasUnattendedUserConfigChange" >{{ availableCurrencies[selectedCurrency].sign }}{{ formatNumberBasedOnSize(props.response.result.exchangeFees) }} paid in exchange fees</div>
          </Transition>
          <Transition
            enter-active-class="duration-400 delay-1200 ease-in-out"
            leave-active-class="duration-200 ease-in-out"
            enter-from-class="translate-y-2 opacity-0"
            leave-to-class="-translate-y-2 opacity-0"
          >
            <div v-if="props.response && !props.hasUnattendedUserConfigChange" >{{ availableCurrencies[selectedCurrency].sign }}{{ formatNumberBasedOnSize(props.response.result.profit) }} profit</div>
          </Transition>
        </div>
      </div>
      <div class="lt-sm:mt-4 sm:mt-1 font-semibold text-xs" >
        <span class="text-brand-gradient">
          ~{{ availableCurrencies[selectedCurrency].sign }}&nbsp;
        </span>
        <Transition
          enter-active-class="duration-400 delay-200 ease-in-out"
          leave-active-class="duration-300 delay-200 ease-in-out"
          enter-from-class="translate-y-2 opacity-0"
          leave-to-class="-translate-y-2 opacity-0"
          mode="out-in"
        >
          <span 
            v-if="props.response && !props.hasUnattendedUserConfigChange" 
            class="text-brand-gradient"
          >
            {{ formatNumberBasedOnSize(props.response.result.profit / periods[props.response.result.period]) }} / {{ $t('day') }} {{ $t('profit') }}
          </span>
          <span
            v-else
            class="text-brand-gradient"
            aria-hidden="true"
          >
            00.00 / {{ $t('day') }} {{ $t('profit') }}
          </span>
        </Transition>        
      </div>

      <div class="mt-6 mb-2 w-full h-.5 bg-brand-primary/10" />
      <div>
      <Transition
        enter-active-class="duration-400 delay-1600 ease-in-out"
        leave-active-class="duration-200 ease-in-out"
        enter-from-class="translate-y-2 opacity-0"
        leave-to-class="-translate-y-2 opacity-0"
      >
        <a 
          v-if="recommendedPlan && !props.hasUnattendedUserConfigChange"
          :href="configLoadedUrl"
          class="space-y-1.5 inline-block flex items-start flex-col group/link"
        >
          <span class="font-semibold text-sm text-brand-secondary">
          <span class="sr-only">{{ $t('go_to') }}&nbsp;</span> {{$t('recommended_plan_for_you')}}
          </span>
          <span class="flex items-center gap-x-2.5">
            <span class="font-bold text-xl">
              DipSway {{ recommendedPlan.label }}, <span class="font-medium text-lg text-brand-gradient"> {{ $t('free_trial_available') }} </span>
            </span>
            <span class="i-material-symbols:keyboard-arrow-right text-xl bg-white group-hover/link:translate-x-1 duration-300 ease-in-out" />
          </span>
          <span class="text-xs font-semibold text-brand-secondary">{{ $t('runs_at') }} <span>€</span>{{ recommendedPlan.pricing.yearly.daily }}/{{ $t('day') }}</span>
        </a>
      </Transition>
      </div>
    </div>
  </div>
</template>